// Pure display helpers for shifts and attendance (architecture sprint-13 §15, sprint 19). Every instant is
// formatted in an explicit zone (the viewer's), never the browser's.
import { addDays, instantToLocal, type IanaZone, type LocalDate } from '@shared/calendar';
import type { ShiftLocationValue, ShiftSnapshot } from '@/types/shifts';
import { formatClock, formatShortLocalDate } from './format';

const MS_PER_SECOND = 1000;

/** ISO instant → 'HH:mm' in a zone. */
export function clockIn(iso: string, tz: IanaZone): string {
  return formatClock(instantToLocal(new Date(iso), tz).second);
}

/** ISO instant → its local date in a zone. */
export function dateIn(iso: string, tz: IanaZone): LocalDate {
  return instantToLocal(new Date(iso), tz).date;
}

/** 'Today' / 'Tomorrow' / 'Yesterday' / 'Mon 2 Nov' relative to `today`. */
export function dayLabel(date: LocalDate, today: LocalDate): string {
  if (date === today) return 'Today';
  if (date === addDays(today, 1)) return 'Tomorrow';
  if (date === addDays(today, -1)) return 'Yesterday';
  return formatShortLocalDate(date);
}

/** '22:00–06:00' (the end may be the next day; the day prefix comes from the start). An open end is '…'. */
export function timeRange(startIso: string, endIso: string | null, tz: IanaZone): string {
  return `${clockIn(startIso, tz)}–${endIso ? clockIn(endIso, tz) : '…'}`;
}

export const LOCATION_LABELS: Record<ShiftLocationValue, string> = { on_site: 'On site', remote: 'Remote' };

/** 'On site · self-declared', or null when no location was recorded. */
export function locationLabel(location: ShiftLocationValue | null, selfDeclared = true): string | null {
  if (location === null) return null;
  return selfDeclared ? `${LOCATION_LABELS[location]} · self-declared` : LOCATION_LABELS[location];
}

/** Elapsed seconds of the open shift: server now at receipt − startedAt, plus the local monotonic delta. */
export function shiftElapsedSeconds(s: ShiftSnapshot, perfNow: number): number {
  if (!s.shift) return 0;
  const atReceipt = s.serverNowMs - Date.parse(s.shift.startedAt);
  return Math.max(0, Math.floor((atReceipt + Math.max(0, perfNow - s.receivedAt)) / MS_PER_SECOND));
}

/** Splits a typed wall-clock span: end ≤ start means the next day (the entry form's "+1 day" rule). */
export function endDateFor(startDate: LocalDate, startTime: string, endTime: string): LocalDate {
  return endTime <= startTime ? addDays(startDate, 1) : startDate;
}
